import React, { useMemo, useState } from "react";

import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Skeleton from "@material-ui/lab/Skeleton";
import Rating from "@material-ui/lab/Rating";

import { makeStyles } from "@material-ui/core/styles";
import { formatMinutes } from "../../pages/Dashboard/format";

const useStyles = makeStyles((theme) => ({
  tableHead: {
    "& th": {
      fontWeight: 700,
      fontSize: "0.72rem",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.background.default,
      borderBottom: `2px solid ${theme.palette.divider}`,
      whiteSpace: "nowrap",
    },
  },
  tableRow: {
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  agentName: {
    fontWeight: 500,
    fontSize: "0.875rem",
    whiteSpace: "nowrap",
  },
  chip: {
    fontWeight: 600,
    fontSize: "0.72rem",
    height: 22,
  },
  chipOnline: {
    color: theme.palette.success.main,
    backgroundColor: `${theme.palette.success.main}1f`,
  },
  chipAway: {
    color: theme.palette.warning.main,
    backgroundColor: `${theme.palette.warning.main}1f`,
  },
  chipOffline: {
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  ratingCell: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    whiteSpace: "nowrap",
  },
  muted: { color: theme.palette.text.secondary },
}));

export function RatingBox({ rating, count }) {
  const classes = useStyles();
  if (rating === null || rating === undefined) {
    return <span className={classes.muted}>Sem avaliações</span>;
  }
  return (
    <span
      className={classes.ratingCell}
      title="1 - Muito insatisfeito · 2 - Insatisfeito · 3 - Regular · 4 - Satisfeito · 5 - Muito satisfeito"
    >
      <Rating value={rating} precision={0.1} max={5} readOnly size="small" />
      <Typography variant="caption" className={classes.muted}>
        {rating.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}
        {count ? ` (${count})` : ""}
      </Typography>
    </span>
  );
}

const COLUMNS = [
  { key: "name", label: "Agente", align: "left" },
  { key: "online", label: "Status", align: "center" },
  { key: "openNow", label: "Atendendo", align: "center" },
  { key: "tickets", label: "Finalizados", align: "center" },
  { key: "avgWaitTime", label: "T.M. Espera", align: "center" },
  { key: "avgSupportTime", label: "T.M. Atendimento", align: "center" },
  { key: "rating", label: "Avaliação", align: "center" },
];

const compare = (a, b, key) => {
  const va = a[key];
  const vb = b[key];
  // Valores vazios sempre no fim.
  if (va === null || va === undefined) return vb === null || vb === undefined ? 0 : 1;
  if (vb === null || vb === undefined) return -1;
  if (typeof va === "string") return va.localeCompare(vb, "pt-BR");
  return Number(va) - Number(vb);
};

export default function TableAttendantsStatus({ loading, attendants }) {
  const classes = useStyles();
  const [orderBy, setOrderBy] = useState("tickets");
  const [order, setOrder] = useState("desc");

  const rows = useMemo(() => {
    const sorted = [...attendants].sort((a, b) => compare(a, b, orderBy));
    if (order === "desc") {
      // Inverte mantendo vazios no fim.
      const filled = sorted.filter((r) => r[orderBy] !== null && r[orderBy] !== undefined);
      const empty = sorted.filter((r) => r[orderBy] === null || r[orderBy] === undefined);
      return [...filled.reverse(), ...empty];
    }
    return sorted;
  }, [attendants, orderBy, order]);

  const handleSort = (key) => {
    if (orderBy === key) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(key);
      setOrder(key === "name" ? "asc" : "desc");
    }
  };

  if (loading) return <Skeleton variant="rect" height={150} style={{ borderRadius: 8 }} />;

  return (
    <>
      <Typography variant="subtitle2" className={classes.sectionTitle}>
        Agentes ({attendants.length})
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead className={classes.tableHead}>
            <TableRow>
              {COLUMNS.map((col) => (
                <TableCell key={col.key} align={col.align} sortDirection={orderBy === col.key ? order : false}>
                  <TableSortLabel
                    active={orderBy === col.key}
                    direction={orderBy === col.key ? order : "asc"}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((a) => (
              <TableRow key={a.id} className={classes.tableRow}>
                <TableCell>
                  <Typography className={classes.agentName}>{a.name}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    label={a.online ? (a.away ? "Ausente" : "Online") : "Offline"}
                    className={`${classes.chip} ${
                      a.online ? (a.away ? classes.chipAway : classes.chipOnline) : classes.chipOffline
                    }`}
                  />
                </TableCell>
                <TableCell align="center">{a.openNow}</TableCell>
                <TableCell align="center">{a.tickets}</TableCell>
                <TableCell align="center">{formatMinutes(a.avgWaitTime)}</TableCell>
                <TableCell align="center">{formatMinutes(a.avgSupportTime)}</TableCell>
                <TableCell align="center">
                  <RatingBox rating={a.rating} count={a.ratingCount} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
